import { QueryClient } from '@tanstack/react-query';

/**
 * 全局唯一的 QueryClient。
 *
 * 抽成独立模块（而不是在 main.tsx 里 new）是为了让 authStore 在会话结束时能 clear()：
 * 换账号登录时，上一个账号的订单 / 客户 / 对账 / 通知等查询结果仍留在内存缓存里，
 * 新账号首屏会先渲染出旧账号的数据（含姓名、电话、地址等 PII），
 * 直到各自的请求返回才被覆盖——共用电脑时这就是跨账号信息泄露。
 */
/**
 * 4xx 是「这个请求本身不对」（记录不存在、无权访问、参数非法），重试三次不会变成对的。
 *
 * react-query v5 的默认 `retry: 3` 带 1s/2s/4s 退避，于是一次 400 要 ~7 秒才落到失败态；
 * 更糟的是退避的空档里 `isLoading`（= isPending && isFetching）已经翻回 false 而 `error` 还是 null，
 * 各页面 `if (error || !data)` 那一支会先渲染写死的兜底文案（把服务端那句中文盖掉），
 * 8 秒后才换成真话——用户看到的是「一直在加载 → 一句错的话 → 才对」。
 * 5xx 与网络抖动仍按原样重试。
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = (error as { status?: number })?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) return false;
  return failureCount < 3;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: shouldRetry },
  },
});
